/**
 * PlayAd "Action Needed" card.
 *
 * Action button runs the server-owned recommendation plan through the original tool gates.
 * Durable receipts and read-only polling distinguish queued/planned work from generated artifacts.
 * Completion is resolved only after verified output; cancellation never replays completed steps.
 * Dismiss requires a reason (training data): `recommendations.dismiss {reason, reasonCode}`.
 */
import { GenerationReceiptPanel } from "@/components/creative/GenerationProgress";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, GitFork, MessageCircleQuestion, Search, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { RecommendationProgress } from "@mimic/core";
import { openAsk } from "@/api/ask-store";
import { callTool, isCancelled, runTool } from "@/api/client";
import { useToolMutation } from "@/api/hooks";
import { toast } from "@/api/toast-store";
import { recCategory, recEvidence, type RecCategory, type Recommendation } from "@/api/types";
import { cn } from "@/lib/cn";
import { humanize, relative, shortDate } from "@/lib/format";
import { Pill } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Chip, Input } from "../ui/Form";
import { Tooltip } from "../ui/Overlay";

const CATEGORY: Record<RecCategory, { label: string; icon: React.ReactNode; cls: string; pillCls: string }> = {
  risk: { label: "Risk", icon: <AlertTriangle className="size-3.5" strokeWidth={2} />, cls: "text-danger", pillCls: "bg-danger-soft text-danger" },
  opportunity: { label: "Opportunity", icon: <TrendingUp className="size-3.5" strokeWidth={2} />, cls: "text-success", pillCls: "bg-success-soft text-success" },
  decision_point: { label: "Decision Point", icon: <GitFork className="size-3.5 rotate-180" strokeWidth={2} />, cls: "text-amber", pillCls: "bg-amber-soft text-amber" },
};

/** Default button label per recommendation kind when the server doesn't send one. */
const KIND_LABEL: Record<string, string> = {
  kill: "Pause & replace",
  scale: "Scale budget",
  iterate: "Make variations",
  rebuild_bench: "Rebuild bench",
  graveyard: "Move to graveyard",
  revive: "Revive",
  retire: "Retire",
  new_concept: "Generate concept",
  creative_refresh: "Refresh creatives",
  test_idea: "Run isolated test",
  budget: "Adjust budget",
  geo: "Review geo",
  fix_tracking: "Fix tracking",
};

export function recActionLabel(r: Recommendation): string {
  return r.action?.label || KIND_LABEL[r.kind] || "Accept";
}

/** Created date (date-only strings are read at local noon so they never shift a day). */
export function recDate(rec: Pick<Recommendation, "createdAt" | "date">): string | undefined {
  if (rec.createdAt) return rec.createdAt;
  if (rec.date) return /^\d{4}-\d{2}-\d{2}$/.test(rec.date) ? `${rec.date}T12:00:00` : rec.date;
  return undefined;
}

const DISMISS_REASONS: { code: string; label: string }[] = [
  { code: "not_relevant", label: "Not relevant" },
  { code: "wrong_data", label: "Wrong data" },
  { code: "already_handled", label: "Already handled" },
  { code: "disagree", label: "Disagree" },
  { code: "too_risky", label: "Too risky" },
  { code: "later", label: "Later" },
  { code: "other", label: "Other" },
];

export function CategoryPill({ rec, date }: { rec: Pick<Recommendation, "category" | "kind" | "severity" | "createdAt" | "date">; date?: boolean }) {
  const c = CATEGORY[recCategory(rec)];
  return (
    <span className="flex items-center gap-2">
      <span className={cn("inline-flex h-6 items-center gap-1 rounded-full px-2 text-[11.5px] font-semibold", c.pillCls)}>
        {c.icon}
        {c.label}
      </span>
      {date && recDate(rec) && <span className="text-[11.5px] text-muted">{shortDate(recDate(rec))}</span>}
    </span>
  );
}

export interface RecommendationCardProps {
  rec: Recommendation;
  brandId: string;
  /** "home" = one-line evidence + relative age (Action Needed); "full" = full evidence + absolute date (drawer). */
  variant?: "home" | "full";
  className?: string;
  /** Show the brand name (All-brands decisions queue). */
  brandLabel?: string;
}

export function RecommendationCard(props: RecommendationCardProps) {
  return <RecommendationCardInner key={`${props.brandId}:${props.rec.id}`} {...props} />;
}

function RecommendationCardInner({ rec, brandId, variant = "home", className, brandLabel }: RecommendationCardProps) {
  const qc = useQueryClient();
  const alive = useRef(true);
  const locked = useRef(false);
  const abort = useRef(new AbortController());
  const [progress, setProgress] = useState<RecommendationProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { alive.current = true; abort.current = new AbortController(); return () => { alive.current = false; abort.current.abort(); }; }, []);
  const [busy, setBusy] = useState(false);
  const [doneLocal, setDoneLocal] = useState<null | "done" | "dismissed" | "accepted">(null);
  const [dismissing, setDismissing] = useState(false);
  const [reasonCode, setReasonCode] = useState("not_relevant");
  const [reasonText, setReasonText] = useState("");
  const dismiss = useToolMutation("recommendations.dismiss", {
    success: "Dismissed — Mimic learns from the reason",
    onSuccess: () => {
      setDoneLocal("dismissed");
      setDismissing(false);
    },
  });
  const evidence = recEvidence(rec);
  const status = doneLocal ?? rec.status;
  const open = status === "open" || status === "accepted" || (status === "done" && doneLocal !== "done" && progress?.state !== "completed");
  const cat = recCategory(rec);
  const created = recDate(rec);
  const age = rec.age ?? (created ? relative(created) : undefined);

  const finish = async (p: RecommendationProgress) => {
    if (!alive.current) return;
    setProgress(p);
    if (p.state !== "completed") return;
    await callTool("recommendations.resolve", { brandId, recommendationId: rec.id, outcome: "done" });
    if (!alive.current) return;
    setDoneLocal("done");
    setError(null);
  };

  // Poll only read-side progress. A remount never repeats a billed or destructive step.
  useEffect(() => {
    if (status !== "accepted" && status !== "done") return;
    if (doneLocal === "done" || error) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      if (locked.current) { timer = setTimeout(refresh, 2000); return; }
      try {
        const p = await callTool<RecommendationProgress>("recommendations.execution", { brandId, recommendationId: rec.id });
        if (cancelled || !alive.current) return;
        // Legacy done records must first acquire a durable execution receipt before resolving again.
        if (status === "done") setProgress(p);
        else await finish(p);
        if (p.state === "running" && !cancelled) timer = setTimeout(refresh, 2000);
      } catch (e) {
        if (!cancelled && alive.current) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [brandId, rec.id, status, doneLocal, error]);

  const runAction = async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError(null);
    try {
      if (!rec.action || rec.action.steps?.length === 0) {
        setProgress({ state: "blocked", message: "No automated action is available. Handle this recommendation manually.", outputs: [], nextStep: null });
        return;
      }
      // Server owns the plan, receipts and result checks; the normal modal still confirms each gate.
      let next: number | undefined = progress?.nextStep ?? 0;
      do {
        const p: RecommendationProgress = await runTool<RecommendationProgress>("recommendations.run", { brandId, recommendationId: rec.id, ...(next !== undefined ? { step: next } : {}) }, { label: recActionLabel(rec), signal: abort.current.signal });
        if (!alive.current) return;
        void qc.invalidateQueries({ queryKey: ["tool"] });
        setDoneLocal("accepted");
        await finish(p);
        next = p.nextStep ?? undefined;
      } while (next !== undefined && alive.current);
    } catch (e) {
      if (!alive.current) return;
      setDoneLocal("accepted");
      if (isCancelled(e)) setError("Confirmation cancelled. Resume when ready; completed steps will not be repeated.");
      else {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
        toast({ tone: "danger", title: `${recActionLabel(rec)} not completed`, body: message });
      }
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  };

  return (
    <article
      className={cn("rounded-xl border border-line/60 bg-elevated/80 p-4 transition-colors", !open && "opacity-70", className)}
      id={`recommendation-${rec.id}`}
      data-testid="recommendation-card"
      aria-label={`${CATEGORY[cat].label}: ${rec.title}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {variant === "full" ? (
            <CategoryPill rec={rec} date />
          ) : (
            <span className={cn("inline-flex items-center gap-1.5 text-[12px] font-semibold", CATEGORY[cat].cls, cat !== "risk" && "rounded-full px-2 py-0.5", cat === "opportunity" && "bg-success-soft", cat === "decision_point" && "bg-amber-soft")}>
              {CATEGORY[cat].icon}
              {CATEGORY[cat].label}
            </span>
          )}
          {brandLabel && <Pill tone="muted" size="xs">{brandLabel}</Pill>}
        </div>
        <Pill tone={open ? "neutral" : status === "dismissed" ? "muted" : "success"} dot={open} icon={!open && status !== "dismissed" ? <CheckCircle2 className="size-3" /> : undefined}>
          {doneLocal === "done" ? "Done" : error ? "Blocked" : progress ? humanize(progress.state) : status === "done" ? "Checking outcome" : status === "open" ? "Open" : humanize(status)}
        </Pill>
      </div>
      <h3 className="mt-2.5 text-[14.5px] font-semibold leading-snug text-balance">{rec.title}</h3>
      {evidence && (
        <div className="mt-2 flex items-center gap-2">
          <Search className="size-3.5 shrink-0 text-muted" />
          <p
            className={cn(
              "min-w-0 flex-1 rounded-md text-[12.5px] text-muted",
              variant === "home" ? "truncate bg-white/[0.04] px-2 py-1" : "leading-relaxed",
            )}
            title={variant === "home" ? evidence : undefined}
          >
            {evidence}
          </p>
          {variant === "home" && age && <span className="shrink-0 text-[11.5px] text-muted">{age}</span>}
        </div>
      )}
      {progress?.outputs.filter(o => o.type === "flow").map(o => <div className="mt-3" key={o.id}><GenerationReceiptPanel brandId={brandId} flowId={o.id} /></div>)}
      {(progress || error) && <div className="mt-3 text-xs" role="status">
        <p>{error ?? progress?.message}</p>
        {progress?.outputs.map(o => <a className="mt-1 block underline" key={`${o.type}:${o.id}`} href={o.url}>{o.label}</a>)}
      </div>}
      {open && dismissing && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-line bg-surface-2/70 p-3">
          <div className="text-[12px] font-medium text-muted">Why dismiss? Mimic stops raising what the team keeps dismissing.</div>
          <div className="flex flex-wrap gap-1.5">
            {DISMISS_REASONS.map((r) => (
              <Chip key={r.code} selected={reasonCode === r.code} onClick={() => setReasonCode(r.code)}>
                {r.label}
              </Chip>
            ))}
          </div>
          <Input value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Optional detail, e.g. “creative already paused manually”" aria-label="Dismiss reason" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDismissing(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={dismiss.isPending}
              data-testid="rec-dismiss-confirm"
              onClick={() =>
                dismiss.mutate({
                  brandId,
                  recommendationId: rec.id,
                  reasonCode,
                  reason: reasonText.trim().length >= 3 ? reasonText.trim() : (DISMISS_REASONS.find((r) => r.code === reasonCode)?.label ?? "Dismissed"),
                })
              }
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {open && !dismissing && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <Tooltip content="Ask Mimic about this">
            <button
              type="button"
              aria-label="Ask Mimic about this recommendation"
              className="mr-auto grid size-8 place-items-center rounded-lg text-subtle hover:bg-hover hover:text-fg"
              onClick={() =>
                openAsk({
                  title: "Ask about this recommendation",
                  prompt: `Explain this recommendation and what you'd do: "${rec.title}"`,
                  context: { recommendation: { id: rec.id, kind: rec.kind, title: rec.title, evidence: rec.evidence, action: rec.action } },
                })
              }
            >
              <MessageCircleQuestion className="size-4" />
            </button>
          </Tooltip>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setDismissing(true)}>
            Dismiss
          </Button>
          <Button size="sm" variant="primary" loading={busy} onClick={() => void runAction()} data-testid="rec-action">
            {progress && ["running", "failed", "blocked"].includes(progress.state) ? "Check progress" : status === "accepted" && !busy ? `Resume: ${recActionLabel(rec)}` : status === "done" ? "Recover action" : recActionLabel(rec)}
          </Button>
        </div>
      )}
    </article>
  );
}

export function RecommendationSkeleton() {
  return (
    <div className="rounded-xl border border-line/60 bg-elevated/50 p-4">
      <div className="flex justify-between">
        <div className="skeleton h-5 w-24 rounded-full" />
        <div className="skeleton h-5 w-14 rounded-full" />
      </div>
      <div className="skeleton mt-3 h-4 w-3/4 rounded" />
      <div className="skeleton mt-2.5 h-6 w-full rounded" />
      <div className="mt-3 flex justify-end gap-2">
        <div className="skeleton h-8 w-20 rounded-lg" />
        <div className="skeleton h-8 w-28 rounded-lg" />
      </div>
    </div>
  );
}
